/*
 * Copyright (c) 2016-2023 VMware, Inc. All Rights Reserved.
 * This software is released under MIT license.
 * The full license information can be found in LICENSE in the root directory of this project.
 */
import { __decorate } from "tslib";
import { html, LitElement } from 'lit';
import { query } from 'lit/decorators/query.js';
import { baseStyles, property, querySlot, querySlotAll, id, event, syncDefinedProps, describeElementByElements, setAttributes, syncProps, calculateOptimalLayout, responsive, } from '@cds/core/internal';
import styles from './control-group.element.scss';
import { getStatusIcon, controlIsWrapped, formLayouts, inlineControlListIsWrapped, defaultFormLayout, defaultControlWidth, getCurrentMessageStatus, } from '../utils/utils.js';
/**
 * Internal Control Group
 *
 * ```typescript
 * import '@cds/core/forms/register.js';
 * ```
 *
 * ```html
 * <cds-internal-control-group>
 *   <label>...</label>
 *   <cds-control>
 *     <label></label>
 *     <input ... />
 *   </cds-control>
 *   <cds-control>
 *     <label></label>
 *     <input ... />
 *   </cds-control>
 * </cds-internal-control-group>
 * ```
 *
 * @element cds-internal-control-group
 * @slot - For projecting control group
 */
let CdsInternalControlGroup = class CdsInternalControlGroup extends LitElement {
    constructor() {
        super(...arguments);
        /**
         * Set the status of control group validation
         * @type {neutral | error | success}
         */
        this.status = 'neutral';
        /** @type {horizontal | horizontal-inline | vertical | vertical-inline | compact} */
        this.layout = defaultFormLayout;
        /** Align the labels of controls within group left or right */
        this.controlAlign = 'left';
        /** Disable all controls within a control group or omit and disable controls individually */
        this.disabled = false;
        /**
         * Adjust the control from the default full width or the browser default width
         * @type {stretch | shrink}
         */
        this.controlWidth = defaultControlWidth;
        /**
         * By default forms will collapse to layout that prevents overflow.
         * If disabled control layout may break or overflow in unexpected ways.
         */
        this.responsive = true;
        this.isInlineControlGroup = false;
        this.isControlGroup = true;
    }
    get messagesTemplate() {
        return html ` <div
      ?hidden=${this.messages.length === 0}
      cds-layout="horizontal align:shrink gap:xs wrap:none"
      class="messages-container"
    >
      ${!this.isInlineControlGroup ? getStatusIcon(this.status) : ''}
      <div class="messages">
        <slot name="message" @slotchange=${this.updateControlMessages}></slot>
      </div>
    </div>`;
    }
    get controlsTemplate() {
        return this.isInlineControlGroup
            ? html `
          <div cds-layout="horizontal gap:xs align:horizontal-stretch" class="input-container">
            <div class="controls" cds-layout="horizontal align:horizontal-stretch wrap:none">
              <slot name="controls"></slot>
            </div>
            ${getStatusIcon(this.status)}
          </div>
        `
            : html `
          <div cds-layout="horizontal align:shrink" class="input-container">
            <div class="controls" cds-layout="${this.inlineControlLayout}">
              <slot name="controls"></slot>
            </div>
          </div>
        `;
    }
    get inlineControlLayout() {
        return `${!this.layout.includes('inline') && this.layout !== 'compact' ? 'vertical gap:xs' : 'horizontal gap:sm'} ${!this.layout.includes('vertical') ? 'wrap:none' : ''}`;
    }
    get primaryLabelLayout() {
        return !this.layout.includes('vertical') ? 'horizontal gap:lg' : 'vertical gap:xs';
    }
    get controlMessageLayout() {
        return `${this.layout === 'compact' ? 'horizontal' : 'vertical'} gap:xs wrap:none align:stretch`;
    }
    render() {
        return html `
      <div class="private-host" cds-layout=${this.primaryLabelLayout}>
        <cds-internal-control-label .disabled=${this.disabled} cds-layout="align:top" action="primary">
          <slot name="label"></slot>
        </cds-internal-control-label>
        <div class="control-message-container" cds-layout=${this.controlMessageLayout}>
          ${this.controlsTemplate} ${this.messagesTemplate}
        </div>
      </div>
    `;
    }
    connectedCallback() {
        super.connectedCallback();
        setAttributes(this, ['role', 'group'], ['cds-control-group', '']);
    }
    firstUpdated(props) {
        super.firstUpdated(props);
        this.associateLabelAndGroup();
        this.setupResponsive();
    }
    updated(props) {
        super.updated(props);
        props.set('isControlGroup', true);
        this.controls.forEach((c) => (c.isControlGroup = true));
        this.messages.forEach(message => syncProps(message, this, { disabled: props.has('disabled') }));
        syncDefinedProps(props, this, Array.from(this.controls));
    }
    get layoutStable() {
        return (!inlineControlListIsWrapped(Array.from(this.controls), this.layout) &&
            !controlIsWrapped(this.controlSlot, this.controlLabel, this.layout));
    }
    associateLabelAndGroup() {
        this.setAttribute('aria-labelledby', this.groupLabelId);
        this.label.setAttribute('id', this.groupLabelId);
    }
    async updateControlMessages() {
        describeElementByElements(this, Array.from(this.messages));
        this.status = await getCurrentMessageStatus(Array.from(this.messages));
    }
    setupResponsive() {
        if (this.responsive) {
            const layoutConfig = { layouts: formLayouts, initialLayout: this.layout };
            this.addEventListener('cdsResizeChange', () => calculateOptimalLayout(this, layoutConfig).then(() => this.layoutChange.emit(this.layout, { bubbles: true })));
        }
    }
};
CdsInternalControlGroup.styles = [baseStyles, styles];
__decorate([
    property({ type: String })
], CdsInternalControlGroup.prototype, "status", void 0);
__decorate([
    property({ type: String })
], CdsInternalControlGroup.prototype, "layout", void 0);
__decorate([
    property({ type: String })
], CdsInternalControlGroup.prototype, "controlAlign", void 0);
__decorate([
    property({ type: Boolean })
], CdsInternalControlGroup.prototype, "disabled", void 0);
__decorate([
    property({ type: String })
], CdsInternalControlGroup.prototype, "controlWidth", void 0);
__decorate([
    property({ type: Boolean })
], CdsInternalControlGroup.prototype, "responsive", void 0);
__decorate([
    querySlot('label', {
        assign: 'label',
        required: 'warning',
        requiredMessage: 'To meet a11y standards a <label> should be provided',
    })
], CdsInternalControlGroup.prototype, "label", void 0);
__decorate([
    querySlotAll('cds-control, [cds-control]')
], CdsInternalControlGroup.prototype, "controls", void 0);
__decorate([
    querySlotAll('cds-control-message')
], CdsInternalControlGroup.prototype, "messages", void 0);
__decorate([
    query('cds-internal-control-label[action=primary]', true)
], CdsInternalControlGroup.prototype, "controlLabel", void 0);
__decorate([
    query('.controls', true)
], CdsInternalControlGroup.prototype, "controlSlot", void 0);
__decorate([
    id()
], CdsInternalControlGroup.prototype, "groupLabelId", void 0);
__decorate([
    event()
], CdsInternalControlGroup.prototype, "layoutChange", void 0);
CdsInternalControlGroup = __decorate([
    responsive()
], CdsInternalControlGroup);
export { CdsInternalControlGroup };
